import type { HireRecommendationsSelection } from "~/lib/marketplace/hire-recommendations-pricing";
import { normalizeRecommendationsSelection } from "~/lib/marketplace/hire-recommendations-pricing";
import type { HireOrganizationLayoutMode } from "~/lib/marketplace/hire-organization-layout";
import {
  AI_EMPLOYEE_PLANS,
  formatAiEmployeePlanPriceLabel,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";
import { inferAiEmployeePlanFromSelection } from "~/lib/siteglue/plan-presets";

export type HireBuilderMarketingBrand = "siteglue" | "inbound";

type HireChannelKey = keyof Pick<
  HireRecommendationsSelection,
  | "liveChat"
  | "browserCalls"
  | "phoneCalls"
  | "textMessages"
  | "email"
  | "videoCalls"
  | "welcomeVideo"
  | "messenger"
  | "whatsapp"
>;

/** Channel labels for Trish hire-builder chat (SiteGlue uses Voice (WebRTC)). */
export function getHireChannelLabels(
  brand?: HireBuilderMarketingBrand | null,
): Array<{ key: HireChannelKey; label: string }> {
  const voiceLabel = brand === "siteglue" ? "Voice (WebRTC)" : "Browser Calls";
  return [
    { key: "liveChat", label: "Live Chat" },
    { key: "browserCalls", label: voiceLabel },
    { key: "phoneCalls", label: "Phone Calls" },
    { key: "textMessages", label: "Text Messages" },
    { key: "email", label: "Email" },
    { key: "videoCalls", label: "Video Calls" },
    { key: "welcomeVideo", label: "Welcome Video" },
    { key: "messenger", label: "Messenger" },
    { key: "whatsapp", label: "WhatsApp" },
  ];
}

export type HireBuilderWidgetColors = {
  headerColor?: string;
  widgetButtonColor?: string;
  launcherBackgroundColor?: string;
  textColor?: string;
};

export type HireBuilderStructuredPayload = {
  type: "hire_builder_update";
  companyName?: string;
  websiteUrl?: string;
  agentName?: string;
  jobTitle?: string;
  widgetColors?: HireBuilderWidgetColors;
  recommendations?: Partial<HireRecommendationsSelection>;
  message?: string;
};

/** In-chat connect / credential UI requested by Trish. */
export type HireBuilderConnectAction = {
  type: "hire_connect";
  integration:
    | "google_calendar"
    | "calendly"
    | "zoom"
    | "juvonno"
    | "nexhealth"
    | "dentrix"
    | "dentrix_ascend"
    | "open_dental"
    | "eaglesoft"
    | "curve"
    | "denticon";
};

const STRUCTURED_BLOCK_RE = /```hire_builder\s*([\s\S]*?)```/i;
const CONNECT_ACTION_BLOCK_RE = /```hire_action\s*([\s\S]*?)```/i;

export function extractHireBuilderPayload(content: string): HireBuilderStructuredPayload | null {
  const match = STRUCTURED_BLOCK_RE.exec(content);
  if (!match?.[1]) return null;

  try {
    const parsed = JSON.parse(match[1].trim()) as HireBuilderStructuredPayload;
    if (parsed.type === "hire_builder_update") return parsed;
  } catch {
    return null;
  }
  return null;
}

export function extractHireBuilderConnectAction(
  content: string,
): HireBuilderConnectAction | null {
  const match = CONNECT_ACTION_BLOCK_RE.exec(content);
  if (!match?.[1]) return null;
  try {
    const parsed = JSON.parse(match[1].trim()) as HireBuilderConnectAction;
    if (parsed.type === "hire_connect" && parsed.integration) return parsed;
  } catch {
    return null;
  }
  return null;
}

export function stripHireBuilderStructuredBlock(content: string): string {
  return content
    .replace(STRUCTURED_BLOCK_RE, "")
    .replace(CONNECT_ACTION_BLOCK_RE, "")
    .trim();
}

function normalizeHexColor(value: string | undefined): string | undefined {
  const raw = value?.trim();
  if (!raw) return undefined;
  const match = raw.match(/^#?([0-9a-fA-F]{6})$/);
  if (!match?.[1]) return undefined;
  return `#${match[1].toLowerCase()}`;
}

export function applyHireBuilderUpdates(args: {
  payload: HireBuilderStructuredPayload;
  companyName: string;
  websiteUrl: string;
  agentName: string;
  jobTitle: string;
  selection: HireRecommendationsSelection;
  widgetColors?: HireBuilderWidgetColors;
}): {
  companyName: string;
  websiteUrl: string;
  agentName: string;
  jobTitle: string;
  selection: HireRecommendationsSelection;
  widgetColors?: HireBuilderWidgetColors;
  changed: boolean;
} {
  let changed = false;
  let companyName = args.companyName;
  let websiteUrl = args.websiteUrl;
  let agentName = args.agentName;
  let jobTitle = args.jobTitle;
  let selection = args.selection;
  let widgetColors = args.widgetColors ? { ...args.widgetColors } : undefined;
  const payload = args.payload;

  if (payload.companyName?.trim() && payload.companyName.trim() !== companyName) {
    companyName = payload.companyName.trim();
    changed = true;
  }
  if (payload.websiteUrl?.trim() && payload.websiteUrl.trim() !== websiteUrl) {
    websiteUrl = payload.websiteUrl.trim();
    changed = true;
  }
  if (payload.agentName?.trim() && payload.agentName.trim() !== agentName) {
    agentName = payload.agentName.trim();
    changed = true;
  }
  if (payload.jobTitle?.trim() && payload.jobTitle.trim() !== jobTitle) {
    jobTitle = payload.jobTitle.trim();
    changed = true;
  }
  if (payload.widgetColors && Object.keys(payload.widgetColors).length > 0) {
    const next: HireBuilderWidgetColors = { ...(widgetColors ?? {}) };
    const headerColor = normalizeHexColor(payload.widgetColors.headerColor);
    const widgetButtonColor = normalizeHexColor(payload.widgetColors.widgetButtonColor);
    const launcherBackgroundColor = normalizeHexColor(
      payload.widgetColors.launcherBackgroundColor,
    );
    const textColor = normalizeHexColor(payload.widgetColors.textColor);

    if (headerColor && headerColor !== next.headerColor) {
      next.headerColor = headerColor;
      changed = true;
    }
    if (widgetButtonColor && widgetButtonColor !== next.widgetButtonColor) {
      next.widgetButtonColor = widgetButtonColor;
      // Keep launcher in sync with button accent unless explicitly set.
      if (!launcherBackgroundColor) {
        next.launcherBackgroundColor = widgetButtonColor;
      }
      changed = true;
    }
    if (launcherBackgroundColor && launcherBackgroundColor !== next.launcherBackgroundColor) {
      next.launcherBackgroundColor = launcherBackgroundColor;
      changed = true;
    }
    if (textColor && textColor !== next.textColor) {
      next.textColor = textColor;
      changed = true;
    }
    widgetColors = next;
  }
  if (payload.recommendations && Object.keys(payload.recommendations).length > 0) {
    const rec = { ...payload.recommendations };
    if (Array.isArray(rec.teamHandoffCtaClusters) && rec.teamHandoffCtaClusters.length > 0) {
      rec.teamHandoffEnabled = true;
    }
    selection = normalizeRecommendationsSelection({
      ...selection,
      ...rec,
    });
    changed = true;
  }

  return { companyName, websiteUrl, agentName, jobTitle, selection, widgetColors, changed };
}

/** Parse "Updated Team Handoff Actions: A, B, C" when the model forgets hire_builder JSON. */
export function extractTeamHandoffActionsFromAssistantText(
  content: string,
): string[] | null {
  const match = content.match(
    /Updated Team Handoff Actions:\s*([^\n.]+)/i,
  );
  if (!match?.[1]) return null;
  const labels = match[1]
    .split(/,| and /i)
    .map((s) => s.replace(/[.*]/g, "").trim())
    .filter((s) => s.length > 0 && s.length < 80);
  return labels.length > 0 ? labels : null;
}

function enabledSkills(
  selection: HireRecommendationsSelection,
  brand?: HireBuilderMarketingBrand | null,
): string[] {
  return getHireChannelLabels(brand)
    .filter((c) => selection[c.key])
    .map((c) => c.label);
}

export type HireBuilderTeamContact = {
  name: string;
  email: string;
  title?: string;
  phone?: string;
};

function resolveLeadLabel(
  leadEmail: string | undefined,
  teamContacts: HireBuilderTeamContact[] | undefined,
): string {
  const email = leadEmail?.trim();
  if (!email) return "Lead not assigned yet";
  const match = teamContacts?.find(
    (c) => c.email.trim().toLowerCase() === email.toLowerCase(),
  );
  if (match?.name?.trim()) {
    const title = match.title?.trim();
    return title ? `${match.name.trim()} (${title}) — ${email}` : `${match.name.trim()} — ${email}`;
  }
  return email;
}

function buildOrganizationRoster(
  selection: HireRecommendationsSelection,
  organizationLayout?: HireOrganizationLayoutMode,
  teamContacts?: HireBuilderTeamContact[],
): {
  mode: "departments" | "locations";
  summaryLabel: string;
  units: Array<{ name: string; lead: string; leadEmail?: string }>;
} {
  const usesDepartments = organizationLayout === "departments";
  if (usesDepartments) {
    const units = (selection.departmentDetails ?? []).map((d) => ({
      name: d.name,
      leadEmail: d.leadTeamMemberEmail,
      lead: resolveLeadLabel(d.leadTeamMemberEmail, teamContacts),
    }));
    return {
      mode: "departments",
      summaryLabel: `${selection.departmentCount} department(s), 1 location`,
      units:
        units.length > 0
          ? units
          : Array.from({ length: Math.max(1, selection.departmentCount) }, (_, i) => ({
              name: `Department ${i + 1}`,
              lead: "Lead not assigned yet",
            })),
    };
  }

  const fromDetails = (selection.locationDetails ?? []).map((l) => ({
    name: l.name?.trim() || "Main Office",
    leadEmail: l.leadTeamMemberEmail,
    lead: resolveLeadLabel(l.leadTeamMemberEmail, teamContacts),
  }));
  const fromList = (selection.locationList ?? []).map((name) => ({
    name,
    lead: "Lead not assigned yet",
  }));
  const units =
    fromDetails.length > 0
      ? fromDetails
      : fromList.length > 0
        ? fromList
        : [{ name: "Main Office", lead: "Lead not assigned yet" }];
  return {
    mode: "locations",
    summaryLabel: `${selection.locationCount} location(s), 1 department`,
    units,
  };
}

/** Markdown list for Trish to paste when asking about organization. */
export function formatOrganizationRosterForPrompt(
  selection: HireRecommendationsSelection,
  organizationLayout?: HireOrganizationLayoutMode,
  teamContacts?: HireBuilderTeamContact[],
): string {
  const roster = buildOrganizationRoster(selection, organizationLayout, teamContacts);
  const unitLabel = roster.mode === "departments" ? "Department" : "Location";
  return roster.units
    .map((u, i) => `${i + 1}. **${unitLabel}: ${u.name}** — Lead Team Member: ${u.lead}`)
    .join("\n");
}

export function buildHireBuilderSystemPrompt(context: {
  businessName: string;
  websiteUrl: string;
  agentName: string;
  jobTitle: string;
  selection: HireRecommendationsSelection;
  widgetColors?: HireBuilderWidgetColors;
  organizationLayout?: HireOrganizationLayoutMode;
  initialPrompt?: string | null;
  /** Known team contacts (employer / lead / extras) for resolving Lead names on org units. */
  teamContacts?: HireBuilderTeamContact[];
  marketingBrand?: HireBuilderMarketingBrand | null;
  /** Explicit SiteGlue plan from consultant, if known. */
  aiEmployeePlanId?: AiEmployeePlanId | null;
}): string {
  const isSiteGlue = context.marketingBrand === "siteglue";
  const productName = isSiteGlue ? "SiteGlue" : "Inbound GPT";
  const skills = enabledSkills(context.selection, context.marketingBrand);
  const channelLabels = getHireChannelLabels(context.marketingBrand);
  const roster = buildOrganizationRoster(
    context.selection,
    context.organizationLayout,
    context.teamContacts,
  );
  const orgUnitLabel = roster.mode === "departments" ? "department" : "location";
  const organizationListMarkdown = formatOrganizationRosterForPrompt(
    context.selection,
    context.organizationLayout,
    context.teamContacts,
  );

  const teamHandoffActions =
    context.selection.teamHandoffCtaClusters?.map((c) => c.ctaQuestion).filter(Boolean) ?? [];

  const priorContext = context.initialPrompt?.trim()
    ? `\n**What the user already told you (pre-signup onboarding with Trish):**\n${context.initialPrompt.trim()}\n`
    : "";

  const extraMembers = context.selection.extraTeamMembers ?? [];
  const settingsSnapshot = formatHireSettingsSnapshotForChat({
    agentName: context.agentName,
    jobTitle: context.jobTitle,
    businessName: context.businessName,
    selection: context.selection,
    organizationLayout: context.organizationLayout,
    teamContacts: context.teamContacts,
    widgetColors: context.widgetColors,
    marketingBrand: context.marketingBrand,
  });

  const inferredPlan =
    context.aiEmployeePlanId && context.aiEmployeePlanId !== "enterprise"
      ? context.aiEmployeePlanId
      : inferAiEmployeePlanFromSelection(context.selection);
  const planMeta = AI_EMPLOYEE_PLANS.find((p) => p.id === inferredPlan);
  const planPrice = formatAiEmployeePlanPriceLabel(inferredPlan);

  const siteGluePlanBlock = isSiteGlue
    ? `
**SiteGlue pricing (IMPORTANT):**
- Plans are **Intern (FREE)**, **Associate ($99/mo)**, **Professional ($299/mo)**, and **Enterprise (Custom)** — not unit add-ons like "$20/mo per tool".
- Current recommended plan from this configuration: **${planMeta?.name ?? inferredPlan} (${planPrice})**.
- Google Calendar, Calendly, Zoom, and similar scheduling connects are **included in Associate+** — never quote separate $20/mo tool fees.
- If the user wants channels above the current plan (e.g. SMS/WhatsApp/Video on Intern or Associate), say they need to **upgrade the plan** (Associate or Professional), then enable the channel via hire_builder.
- Channel naming: use **Live Chat** (never "Chat"), **Voice (WebRTC)** (never "Browser Calls"), **Video Calls**, **Phone Calls**.
`
    : "";

  const identityLine = isSiteGlue
    ? `You are **Trish**, the SiteGlue AI Solutions Consultant. You already analyzed their website, recommended a plan, chose an agent profile, and generated a live Preview. Continue as the same guide — the prior transcript is in this chat.`
    : `You are **Trish**, the Inbound GPT onboarding agent. You already analyzed their website, chose an agent profile, and generated a live Preview. Continue as the same guide — the prior transcript is in this chat.`;

  const disabledChannelHints = channelLabels
    .filter((c) => !context.selection[c.key])
    .map((c) => c.label)
    .join(", ");

  return `${identityLine}
${priorContext}
${siteGluePlanBlock}
**Your role:**
- Stay in character as Trish — warm, concise, and practical.
- Always use the agent name **${context.agentName}** (never invent a different name like Samantha, Virginia, or Sunday unless that is the configured agentName). Never put a different agentName in hire_builder JSON.
- The first assistant message after Preview already confirms what was generated and asks question **1**. Continue from there — do **not** restart with "Hi again" or Company Information.
- Walk through the sequence below **one question at a time**. Wait for the user's answer before moving to the next number.
- When the user asks you to change settings, output a structured update block (see below) — never claim you changed something without the block.
- **Chat-first:** Complete setup in this conversation. Do **not** tell users to open the Configure tab for routine changes. They can test in **Preview**; you list settings and collect details here.
- After applying changes, briefly restate what changed (list the updated values in chat) and note that Preview updates automatically.
${isSiteGlue ? `- Prefer plan language (${productName} Intern / Associate / Professional) over à-la-carte skill prices.` : ""}

**Guided walkthrough sequence (one question per turn; skip items already confirmed):**
1. **Channels** — list currently enabled channels using labels: ${channelLabels.map((c) => c.label).join(", ")}. Then offer disabled ones${disabledChannelHints ? ` (available to add: ${disabledChannelHints})` : ""}.
2. **Appointments** — ask if they want scheduling; if yes, ask which platform (Google Calendar, Calendly, Zoom, Juvonno, NexHealth, or other). Then emit a \`hire_action\` connect block so the UI shows Connect / credential fields in chat.${isSiteGlue ? " Do **not** mention $20/mo — scheduling is part of the plan." : ""}
3. **Organization** — paste the full list below (every ${orgUnitLabel} + Lead Team Member). Ask if it looks correct. NEVER summarize as only a count (e.g. "I see 4 departments").
4. **Team members** — Currently ${context.selection.teamMemberCount} team member(s) receive conversation reports. If they want to add Leads for other ${orgUnitLabel}s, ask for **Name, Title, Email, and Phone** for each person (and which ${orgUnitLabel} they oversee). Do **not** ask for names alone — wait until you have all four fields before applying an update.
5. **Widget colors** — offer to update header / button colors (ask for hex values or describe; apply via hire_builder)
6. **Team Handoff Actions** — offer to add or edit CTAs like Connect with Staff
7. **Final step** — ask: **"Are you ready to onboard ${context.agentName}?"** and mention the **Onboard ${context.agentName}** tab (or **Reset to Free**).

**Required fields — always collect before applying an update:**
- **Team member:** Name, Title, Email, Phone, and which ${orgUnitLabel} they oversee
- **Location (when adding/editing):** Name, Address, City, State, Postal code, Phone, Business hours, and Lead Team Member (Name + Email at minimum)
- **Department (when adding/editing):** Department name and Lead Team Member (Name, Title, Email, Phone)
- **Appointments:** Platform choice, then use \`hire_action\` so the user can Connect (Google) or enter API credentials (Calendly, Zoom, Juvonno, NexHealth) in chat
- If any required field is missing, ask for the missing fields in one clear list — do not apply a partial update

**Organization list to paste for question 3:**
${organizationListMarkdown}

**Settings snapshot (paste or paraphrase when confirming — users should not need Configure):**
${settingsSnapshot}

**Current configuration (JSON):**
${JSON.stringify(
  {
    product: productName,
    ...(isSiteGlue
      ? { recommendedPlan: planMeta?.name ?? inferredPlan, planPrice }
      : {}),
    companyName: context.businessName,
    websiteUrl: context.websiteUrl,
    agentName: context.agentName,
    jobTitle: context.jobTitle,
    widgetColors: context.widgetColors ?? {},
    responsibilities: skills,
    training: context.selection.agentTrainingAdvanced ? "Basic + Advanced" : "Basic",
    organizationMode: roster.mode,
    organizationSummary: roster.summaryLabel,
    organizationUnits: roster.units,
    teamMemberCount: context.selection.teamMemberCount,
    teamContacts: context.teamContacts ?? [],
    extraTeamMembers: extraMembers,
    appointmentsEnabled: context.selection.appointmentsEnabled,
    schedulingIntegrationId: context.selection.schedulingIntegrationId,
    emrIntegrationEnabled: context.selection.emrIntegrationEnabled,
    emrIntegrationId: context.selection.emrIntegrationId,
    teamHandoffEnabled: context.selection.teamHandoffEnabled,
    teamHandoffActions,
    initialRoleRequest: context.initialPrompt ?? null,
  },
  null,
  2,
)}

**Structured updates — when changing settings, append:**

\`\`\`hire_builder
{
  "type": "hire_builder_update",
  "companyName": "<optional>",
  "websiteUrl": "<optional>",
  "agentName": "<optional>",
  "jobTitle": "<optional>",
  "widgetColors": {
    "headerColor": "#09203f",
    "widgetButtonColor": "#09203f"
  },
  "recommendations": {
    "liveChat": true,
    "phoneCalls": false,
    "appointmentsEnabled": true,
    "schedulingIntegrationId": "google_calendar",
    "teamMemberCount": 2,
    "extraTeamMembers": [
      {
        "name": "Jane Doe",
        "title": "Admissions Director",
        "email": "jane@school.org",
        "phone": "555-0100",
        "departmentName": "Admissions"
      }
    ]
  }
}
\`\`\`

**Connect UI in chat — after the user picks a scheduling/EMR platform, also append:**

\`\`\`hire_action
{
  "type": "hire_connect",
  "integration": "google_calendar"
}
\`\`\`

Valid \`integration\` values: google_calendar, calendly, zoom, juvonno, nexhealth, dentrix, dentrix_ascend, open_dental, eaglesoft, curve, denticon.

Only include fields you are changing. For colors, use 6-digit hex (with #). \`widgetButtonColor\` also updates the floating launcher. When adding team members, include full \`extraTeamMembers\` entries with name, title, email, phone, and \`departmentName\` (or \`locationName\`) for the unit they oversee. When adding/updating Team Handoff Actions (CTA buttons), always include \`teamHandoffEnabled: true\` and the full \`teamHandoffCtaClusters\` array so the live widget updates — never claim a CTA was added unless it is in the hire_builder block. Valid \`recommendations\` keys: liveChat, textMessages, email, browserCalls, videoCalls, phoneCalls, welcomeVideo, messenger, whatsapp, appointmentsEnabled, agentTrainingAdvanced, schedulingIntegrationId, emrIntegrationEnabled, emrIntegrationId, locationCount, departmentCount, teamMemberCount, teamHandoffEnabled, teamHandoffCtaClusters, extraTeamMembers.

Example Team Handoff Actions update:
\`\`\`hire_builder
{
  "type": "hire_builder_update",
  "recommendations": {
    "teamHandoffEnabled": true,
    "teamHandoffCtaClusters": [
      { "ctaQuestion": "Schedule Service", "qualificationQuestions": ["What service do you need?", "What is the best phone number to reach you?"], "locationRouting": "all" },
      { "ctaQuestion": "Request a Quote", "qualificationQuestions": ["What are you looking for a quote on?", "What is your email?"], "locationRouting": "all" },
      { "ctaQuestion": "Emergency Service", "qualificationQuestions": ["What is the emergency?", "What is the best callback number?"], "locationRouting": "all" }
    ]
  }
}
\`\`\`

**Rules:**
- Ask one numbered question at a time, but **always list the relevant current settings** in that message (channels list, org roster, team members, appointment platform, colors) so the user does not need Configure.
- For question 3, paste the Organization list above verbatim. Each line must include the ${orgUnitLabel} name and Lead Team Member.
- For question 4, if any of Name / Title / Email / Phone is missing, ask for the missing fields before applying a team-member update.
- For appointments, after they choose a platform: apply hire_builder (appointmentsEnabled + schedulingIntegrationId or emrIntegrationId) **and** emit hire_action so Connect / credential fields appear in chat.
- The UI seeds a confirmation after Preview generates. Continue from the user's reply; do **not** re-introduce yourself or say "Hi again".
${isSiteGlue ? "- On SiteGlue, do **not** ask about adding phone/SMS/other channels as the first question after preview. Start from appointments / organization / team / colors / Team Handoff Actions as needed." : ""}
- Never claim a color, CTA, or setting changed unless you included it in the \`hire_builder\` block.
- Never mention Stripe — onboarding checkout is handled in the Onboard tab.
${isSiteGlue ? "- Never quote unit skill, tool, or per-seat prices ($15/mo, $20/mo, etc.). Use Pricing Plans only: Intern (Free), Associate ($49/month), Professional ($99/month)." : ""}
- Use plain text company names (no HTML entities).`;
}

/** Human-readable settings snapshot Trish should paste in chat (not Configure-only). */
export function formatHireSettingsSnapshotForChat(opts: {
  agentName: string;
  jobTitle: string;
  businessName: string;
  selection: HireRecommendationsSelection;
  organizationLayout?: HireOrganizationLayoutMode;
  teamContacts?: HireBuilderTeamContact[];
  widgetColors?: HireBuilderWidgetColors;
  marketingBrand?: HireBuilderMarketingBrand | null;
}): string {
  const selection = opts.selection;
  const channelLabels = getHireChannelLabels(opts.marketingBrand);
  const enabled = channelLabels.filter((c) => selection[c.key]).map((c) => c.label);
  const orgList = formatOrganizationRosterForPrompt(
    selection,
    opts.organizationLayout,
    opts.teamContacts,
  );
  const scheduling =
    selection.appointmentsEnabled && selection.schedulingIntegrationId !== "none"
      ? selection.schedulingIntegrationId.replace(/_/g, " ")
      : selection.emrIntegrationEnabled && selection.emrIntegrationId !== "none"
        ? selection.emrIntegrationId.replace(/_/g, " ")
        : "not enabled";
  const extras = (selection.extraTeamMembers ?? [])
    .map((m) => {
      const bits = [m.name, m.title, m.email, m.phone].filter(Boolean);
      const unit = m.departmentName || m.locationName;
      return unit ? `${bits.join(" · ")} (${unit})` : bits.join(" · ");
    })
    .filter(Boolean);
  const colors = opts.widgetColors;
  const colorLine =
    colors?.headerColor || colors?.widgetButtonColor
      ? `Header ${colors.headerColor ?? "—"} · Button ${colors.widgetButtonColor ?? "—"}`
      : "default";

  const lines = [
    `**Current settings for ${opts.agentName}**`,
    `- Role: ${opts.jobTitle} at ${opts.businessName}`,
    `- Channels: ${enabled.length ? enabled.join(", ") : "none"}`,
    `- Appointments: ${scheduling}`,
    `- Organization:`,
    orgList || "  (none listed)",
    `- Extra team members: ${extras.length ? extras.join("; ") : "none yet"}`,
    `- Widget colors: ${colorLine}`,
  ];
  return lines.join("\n");
}

function formatChannelList(labels: string[]): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
}

function formatEnabledChannelsPhrase(labels: string[]): string {
  if (labels.length === 0) return "engage website visitors";
  const lower = labels.map((l) => l.toLowerCase());
  const hasChat = lower.some((l) => l.includes("chat"));
  const hasTalk = lower.some((l) => l.includes("browser") || l.includes("phone"));
  if (hasChat && hasTalk) {
    return "chat live or talk with website visitors";
  }
  if (hasChat) return "chat live with website visitors";
  if (hasTalk) return "talk with website visitors";
  return `engage website visitors via ${formatChannelList(labels).toLowerCase()}`;
}

/**
 * Post-preview Trish message: confirm what was generated, then start step-by-step
 * onboarding at question 1 (additional channels).
 */
export function buildTrishPostPreviewConfirmationMessage(opts: {
  agentName: string;
  jobTitle: string;
  businessName: string;
  selection: HireRecommendationsSelection;
  organizationLayout?: HireOrganizationLayoutMode;
  marketingBrand?: HireBuilderMarketingBrand | null;
  aiEmployeePlanId?: AiEmployeePlanId | null;
}): string {
  const agent = opts.agentName.trim() || "your agent";
  const isSiteGlue = opts.marketingBrand === "siteglue";
  const role =
    opts.jobTitle.trim() || (isSiteGlue ? "SiteGlue AI Employee" : "Inbound GPT Agent");
  const business = opts.businessName.trim() || "your business";
  const selection = opts.selection;
  const channelLabels = getHireChannelLabels(opts.marketingBrand);

  const enabled = channelLabels.filter((c) => selection[c.key]).map((c) => c.label);
  const disabled = channelLabels.filter((c) => !selection[c.key]).map((c) => c.label);

  const usesDepartments = opts.organizationLayout === "departments";
  const orgUnit = usesDepartments ? "department" : "location";

  const engagement = formatEnabledChannelsPhrase(enabled);

  const inferredPlan =
    opts.aiEmployeePlanId && opts.aiEmployeePlanId !== "enterprise"
      ? opts.aiEmployeePlanId
      : inferAiEmployeePlanFromSelection(selection);
  const planMeta = AI_EMPLOYEE_PLANS.find((p) => p.id === inferredPlan);
  const planPrice = formatAiEmployeePlanPriceLabel(inferredPlan);

  if (isSiteGlue) {
    const primaryCtas = (selection.teamHandoffCtaClusters ?? [])
      .map((c) => c.ctaQuestion.trim())
      .filter(Boolean)
      .slice(0, 2);
    const ctaA = primaryCtas[0] || "Request Service";
    const ctaB = primaryCtas[1] || "Request a Quote";
    return [
      `I generated **${agent}**, your new ${role} at ${business}, to ${engagement} and deliver detailed conversation reports to the Lead Team Member for each ${orgUnit}.`,
      "",
      `**Recommended plan:** ${planMeta?.name ?? inferredPlan} (${planPrice})`,
      "",
      `**Channels currently enabled:** ${enabled.length ? enabled.join(", ") : "none"}`,
      "",
      `Your new ${role} agent is ready for your review and testing. Click on **${ctaA}** or **${ctaB}** to see how ${agent} handles inquiries, gathers information and emails a detailed conversation report to your team.`,
    ].join("\n");
  }

  const lines: string[] = [
    `I generated **${agent}**, your new ${role} at ${business}, to ${engagement} and deliver detailed conversation reports to the Lead Team Member for each ${orgUnit}.`,
    "",
    `**Channels currently enabled:** ${enabled.length ? enabled.join(", ") : "none"}`,
    "",
    `You can test ${agent} in the **Preview** tab anytime. We'll finish setup here in chat — I'll list settings as we go and ask one question at a time:`,
    "",
  ];

  if (disabled.length > 0) {
    lines.push(
      `1. Would you also like ${agent} to answer phone calls or engage visitors on other channels? Currently available to add: **${formatChannelList(disabled)}**.`,
    );
  } else {
    lines.push(
      `1. ${agent} already has all channel skills enabled. Want to change any of them, or shall we move on to appointments?`,
    );
  }

  lines.push(
    "",
    "_Next: appointments (with Connect in chat), organization, team members, widget colors, and Team Handoff Actions — one at a time._",
  );

  return lines.join("\n");
}

/** Follow-up after SiteGlue post-preview confirmation (sent after a short wait). */
export function buildTrishPostPreviewFollowUpMessage(agentName: string): string {
  const agent = agentName.trim() || "your AI Employee";
  return `Do you have any comments or changes for **${agent}**? Just let me know and I'll update. If everything looks good, we can continue setup — or I can walk you through upgrade features available on Associate and Professional.`;
}

/** @deprecated Use buildTrishPostPreviewConfirmationMessage */
export function buildTrishPostAuthWelcomeMessage(opts: {
  agentName: string;
  jobTitle: string;
  businessName?: string;
  selection?: HireRecommendationsSelection;
  organizationLayout?: HireOrganizationLayoutMode;
}): string {
  if (opts.selection) {
    return buildTrishPostPreviewConfirmationMessage({
      agentName: opts.agentName,
      jobTitle: opts.jobTitle,
      businessName: opts.businessName ?? "your business",
      selection: opts.selection,
      organizationLayout: opts.organizationLayout,
    });
  }
  const agent = opts.agentName.trim() || "your agent";
  const role = opts.jobTitle.trim() || "Inbound GPT";
  return `I generated **${agent}**, your new ${role}. You can test ${agent} in the Preview tab. Reply here and I'll walk you through the next setup steps — starting with additional channels.`;
}
